import React, { useEffect, useRef, useState } from "react";
import { CustomEase, Flip } from "gsap/all";
import { useTranslation } from "react-i18next";
import { IntroSectionData } from "../../../types/IntroSectionData";
import { Tabs } from "../VercelTabs/VercelTabs";
import gsap from "gsap";
import "./IntroSectionBottom.css";

gsap.registerPlugin(CustomEase, Flip);

interface IntroSectionBottomProps {
  data: IntroSectionData;
  translationNamespace?: string;
}

const IntroSectionBottom: React.FC<IntroSectionBottomProps> = ({ 
  data,
  translationNamespace = 'privateCloud' 
}) => {
  const { t } = useTranslation();
  const { sectionTitle, sectionSubtitle, tabsData, fixedImageSrc, fixedImageAlt } = data;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const actualNamespace = data.translationNamespace || translationNamespace;
  
  console.log("Using translation namespace:", actualNamespace);
  console.log("Tab data:", tabsData);

  const bgRef = useRef<HTMLDivElement | null>(null);

  const getTabTranslationKey = (title: string) => {
    let key = '';
    switch(title.toLowerCase()) {
      case 'expertise':
        key = 'expertise';
        break;
      case 'sécurité':
      case 'securite':
      case 'security':
        key = 'security';
        break;
      case 'déploiement':
      case 'deploiement':
      case 'deployment':
        key = 'deployment';
        break;
      case 'sauvegarde':
      case 'backup':
        key = 'backup';
        break;
      case 'automatisation':
      case 'automation':
        key = 'automatisation';
        break;
      case 'accompagnement':
      case 'support':
        key = 'accompagnement';
        break;
      case 'audit':
        key = 'audit';
        break;
      case 'stratégie':
      case 'strategie':
      case 'strategy':
        key = 'strategie';
        break;
      case 'méthodologies':
      case 'methodologies':
        key = 'methodologies';
        break;
      case 'sécurisation':
      case 'securisation':
        key = 'securisation';
        break;
      case 'maîtrise':
      case 'maitrise':
      case 'mastery':
        key = 'maitrise';
        break;
      case 'orchestration':
        key = 'orchestration';
        break;
      case 'intégration':
      case 'integration':
        key = 'integration';
        break;
      case 'savoir-faire':
      case 'know-how':
        key = 'expertise2';
        break;
      case 'optimisation':
      case 'optimization':
        key = 'optimization';
        break;
      case 'expérience':
      case 'experience':
        key = 'expertise';
        break;
      default:
        key = title.toLowerCase();
        break;
    }
    console.log(`Translation key for "${title}": ${key}`);
    return key;
  };

  const getActiveTabHeading = () => {
    if (tabsData && tabsData.length > 0 && tabsData[activeTab]) {
      const tab = tabsData[activeTab];
      const transKey = tab.key || getTabTranslationKey(tab.title);
      const translationPath = `${actualNamespace}.expertise.tabs.${transKey}`;
      return t(`${translationPath}.heading`, tab.heading);
    }
    return sectionSubtitle ? t(`${actualNamespace}.expertise.sectionSubtitle`, sectionSubtitle) : '';
  };

  // Préparer les tabs pour le composant VercelTabs
  const vercelTabs = tabsData.map((tab, index) => {
    const transKey = tab.key || getTabTranslationKey(tab.title);
    const translationPath = `${actualNamespace}.expertise.tabs.${transKey}`;
    return {
      id: tab.id.toString(),
      label: t(`${translationPath}.title`, tab.title)
    };
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (tabId: string) => {
    const index = tabsData.findIndex(tab => tab.id.toString() === tabId);
    if (index !== -1 && index !== activeTab && !isAnimating) {
      setIsAnimating(true);
      setActiveTab(index);
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  return (
    <section className="intro-section-bottom">
    <div className="section-bottom">
      <div data-tabs="wrapper" className="tab-layout" ref={wrapperRef}>
        <div className="tab-layout-col">
          <div className="tab-layout-container">
            <div className="tab-container">
              <div className="tab-container-top">
                <div className="intro-row-2"></div>
      
                <div className="tabs-content-container">
                <div className="intro-text-bottom">
                  <h2>{t(`${actualNamespace}.expertise.sectionTitle`, sectionTitle)}</h2>
                </div>
                  <div className="tab-buttons-container">
                    <Tabs
                      tabs={vercelTabs}
                      activeTab={tabsData[activeTab]?.id.toString()}
                      onTabChange={handleTabChange}
                      className="vercel-tabs-wrapper"
                    />
                  </div>
                  
                  <div className="tab-container-bottom">
                    <div className="tab-content-wrap">
                      {tabsData.map((tab, index) => {
                        const transKey = tab.key || getTabTranslationKey(tab.title);
                        const translationPath = `${actualNamespace}.expertise.tabs.${transKey}`;
                        return (
                          <div
                            key={tab.id}
                            className={`tab-content-item ${activeTab === index ? "active" : ""}`}
                            style={{ 
                              display: activeTab === index ? 'block' : 'none',
                              position: 'static',
                              transition: 'none',
                              transform: 'none'
                            }}
                          >
                            <div className="tab-content-text-container">
                              {(() => {
                                const text1 = t(`${translationPath}.text1`, '');
                                const text2 = t(`${translationPath}.text2`, '');
                                
                                if (text1 && text2) {
                                  return (
                                    <>
                                      <p className="content-p opacity--80 texte-1">
                                        {text1}
                                      </p>
                                      <p className="content-p opacity--80">
                                        {text2}
                                      </p>
                                    </>
                                  );
                                }
                                
                                const fullText = t(`${translationPath}.text`, tab.text);
                                const textParts = fullText.split('\n').filter(part => part.trim());
                                
                                if (textParts.length >= 2) {
                                  return (
                                    <>
                                      <p className="content-p opacity--80">
                                        {textParts[0].trim()}
                                      </p>
                                      <p className="content-p opacity--80">
                                        {textParts.slice(1).join(' ').trim()}
                                      </p>
                                    </>
                                  );
                                }
                                
                                const sentences = fullText.split('.').filter(s => s.trim());
                                if (sentences.length >= 2) {
                                  const midpoint = Math.ceil(sentences.length / 2);
                                  return (
                                    <>
                                      <p className="content-p opacity--80">
                                        {sentences.slice(0, midpoint).join('.').trim() + '.'}
                                      </p>
                                      <p className="content-p opacity--80">
                                        {sentences.slice(midpoint).join('.').trim() + '.'}
                                      </p>
                                    </>
                                  );
                                }
                                
                                return (
                                  <>
                                    <p className="content-p opacity--80">
                                      {fullText}
                                    </p>
                                    <p className="content-p opacity--80">
                                    </p>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tab-layout-col">
          <div className="tab-visual-wrap fixed-visual-wrap">
            <img
              src={fixedImageSrc}
              loading="lazy"
              className="tab-image fixed-tab-image"
              alt={t(`${actualNamespace}.expertise.imageAlt`, fixedImageAlt)}
            />
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

export default IntroSectionBottom;